import config from '../../config.json'
import get, { getAtomicSchemasForCollection } from '../helpers/Api'

const COLLECTION_SCHEMA_NAMES_CACHE_TTL = 1000 * 60 * 1 // 1 min

export const loadCollectionData = async (collectionUniqueName) => {
    const response = await get(`collection/${collectionUniqueName}`, 'wax-api')
    if (!response || collectionUniqueName === 'new') {
        return null
    }

    const collectionData = {
        uniqueName: response.collection,
        displayName: response.name,
        image: response.image.replace(config.ipfs, ''),
        marketFee: response.marketFee,
        url: response.url,
        description: response.description,
    }

    return collectionData
}

const parseSchemaNames = (
    data,
    collectionUniqueName,
    setCollectionSchemaNamesCache,
) => {
    let schemaNames = []
    for (const schema of data) {
        schemaNames.push(schema.schema_name)
    }
    setCollectionSchemaNamesCache({
        updated: Date.now(),
        value: schemaNames,
        key: `${collectionUniqueName}_schema_names`,
        invalid: false,
    })
    return schemaNames
}

export const loadCollectionSchemaNames = async (
    collectionUniqueName,
    collectionSchemaNamesCache,
    setCollectionSchemaNamesCache,
) => {
    if (collectionUniqueName === 'new') {
        return []
    }
    if (
        collectionSchemaNamesCache &&
        collectionSchemaNamesCache.key ===
            `${collectionUniqueName}_schema_names` &&
        collectionSchemaNamesCache.invalid === false
    ) {
        const { updated, value } = collectionSchemaNamesCache
        if (
            value &&
            updated &&
            Date.now() - updated < COLLECTION_SCHEMA_NAMES_CACHE_TTL
        ) {
            return value
        } else {
            return getAtomicSchemasForCollection(collectionUniqueName).then(
                (res) =>
                    parseSchemaNames(
                        res,
                        collectionUniqueName,
                        setCollectionSchemaNamesCache,
                    ),
            )
        }
    } else {
        return getAtomicSchemasForCollection(collectionUniqueName).then((res) =>
            parseSchemaNames(
                res,
                collectionUniqueName,
                setCollectionSchemaNamesCache,
            ),
        )
    }
}

const COLLECTION_SCHEMA_STATS_CACHE_TTL = 1000 * 60 * 1 // 1 min

const getSchemaStats = async (collectionUniqueName) => {
    let length = 1000

    let data = []

    let page = 1
    while (length === 1000) {
        const response = await fetch(
            (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? config.atomictest
                : config.atomic) +
                `/atomicassets/v1/templates?collection_name=${collectionUniqueName}&limit=1000&order=asc&page=${page}`,
        )
        const json = await response.json()
        const res = json.data

        length = res && res.length

        data = data.concat(res)

        page += 1
    }
    return data
}

const parseSchemaStats = (
    data,
    collectionUniqueName,
    setCollectionSchemaStatsCache,
) => {
    const schemaStats = []
    for (const template of data) {
        const templateSchemaName = template.schema.schema_name
        const mints = parseInt(template.issued_supply)

        const schemaStatsElement = schemaStats.find(
            (schema) => schema.schemaName === templateSchemaName,
        )

        if (!schemaStatsElement) {
            schemaStats.push({
                schemaName: templateSchemaName,
                templateAmount: 1,
                mintAmount: mints,
            })
        } else {
            schemaStatsElement.templateAmount += 1
            schemaStatsElement.mintAmount += mints
        }
    }

    setCollectionSchemaStatsCache({
        updated: Date.now(),
        value: schemaStats,
        key: `${collectionUniqueName}_schema_stats`,
        invalid: false,
    })

    return schemaStats
}

export const loadCollectionSchemaStats = async (
    collectionUniqueName,
    collectionSchemaStatsCache,
    setCollectionSchemaStatsCache,
) => {
    if (collectionUniqueName === 'new') return []
    if (
        collectionSchemaStatsCache &&
        collectionSchemaStatsCache.key ===
            `${collectionUniqueName}_schema_stats` &&
        collectionSchemaStatsCache.invalid === false
    ) {
        const { updated, value } = collectionSchemaStatsCache
        if (
            value &&
            updated &&
            Date.now() - updated < COLLECTION_SCHEMA_STATS_CACHE_TTL
        ) {
            return value
        } else {
            return getSchemaStats(collectionUniqueName).then((res) =>
                parseSchemaStats(
                    res,
                    collectionUniqueName,
                    setCollectionSchemaStatsCache,
                ),
            )
        }
    } else {
        return getSchemaStats(collectionUniqueName).then((res) =>
            parseSchemaStats(
                res,
                collectionUniqueName,
                setCollectionSchemaStatsCache,
            ),
        )
    }
}

const getSchemaTemplates = async (collectionName, schemaName) => {
    const res = await get(
        `schema-templates/${collectionName}/${schemaName}`,
        'legacy',
    )
    return res ? res : []
}

const parseSchemaTemplates = (data) => {
    const schemaTemplates = []
    for (const template of data) {
        schemaTemplates.push({
            templateId: template.templateId,
            templateImage: template.image,
            templateName: template.name,
            templateBurnable: template.burnable,
            templateTransferable: template.transferable,
            templateIssuedSupply: template.issuedSupply,
            templateMaxSupply: template.maxSupply,
        })
    }
    schemaTemplates.sort((a, b) => {
        return b.templateId - a.templateId
    })
    return schemaTemplates
}

export const loadSchemaTemplates = async (collectionUniqueName, schema) => {
    if (schema === 'new') {
        return []
    } else {
        return getSchemaTemplates(collectionUniqueName, schema).then((res) =>
            parseSchemaTemplates(res),
        )
    }
}

const getTemplate = async (collectionUniqueName, templateId) => {
    const response = await fetch(
        (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
            ? config.atomictest
            : config.atomic) +
            `/atomicassets/v1/templates/${collectionUniqueName}/${templateId}`,
    )
    const json = await response.json()

    return json.data ? json.data : null
}

const parseTemplate = (templateData) => {
    const template = {
        templateId: templateData.template_id,
        templateImage: templateData.immutable_data.img,
        templateVideo: templateData.immutable_data.video,
        templateName: templateData.immutable_data.name,
        templateBurnable: templateData.is_burnable,
        templateTransferable: templateData.is_transferable,
        templateIssuedSupply: templateData.issued_supply,
        templateMaxSupply: templateData.max_supply,
    }
    return template
}

export const loadTemplate = async (collectionUniqueName, templateId) => {
    return getTemplate(collectionUniqueName, templateId).then((res) =>
        parseTemplate(res),
    )
}

export const loadCachedAtomicQuery = async (
    url,
    cacheKey,
    cache,
    setCache,
    ttl,
) => {
    const endpoint =
        (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
            ? config.atomictest
            : config.atomic) + `/atomicassets/v1${url}`

    if (cache && cache.key === cacheKey && cache.invalid === false) {
        const { updated, value } = cache
        if (value && updated && Date.now() - updated < ttl) {
            return value
        }
    }

    const response = await fetch(endpoint)
    const json = await response.json()

    setCache({
        updated: Date.now(),
        value: json.data,
        key: cacheKey,
        invalid: false,
    })

    return json.data
}
